import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Brain, AlertTriangle, Eye, Clock, Pill, Activity, Moon,
  TrendingUp, TrendingDown, ChevronLeft, Loader2, Calendar,
  ArrowRight, Info, Sparkles, ShieldCheck, Shield, ShieldAlert,
  User, FileText, Target, BarChart3
} from 'lucide-react';

const SEVERITY_CONFIG = {
  critical: { color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30', label: 'Critical' },
  high: { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', label: 'High' },
  medium: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', label: 'Medium' },
  low: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Low' },
};

const CATEGORY_CONFIG = {
  medication: { icon: Pill, label: 'Medication', color: 'text-purple-400' },
  symptoms: { icon: Activity, label: 'Symptoms', color: 'text-blue-400' },
  correlation: { icon: Brain, label: 'Correlation', color: 'text-indigo-400' },
  timing: { icon: Clock, label: 'Timing', color: 'text-amber-400' },
  risk: { icon: AlertTriangle, label: 'Risk', color: 'text-rose-400' },
  general: { icon: Info, label: 'General', color: 'text-white/50' },
};

const STATE_CONFIG = {
  stable: { icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Stable' },
  watch: { icon: Shield, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Watch' },
  at_risk: { icon: ShieldAlert, color: 'text-rose-400', bg: 'bg-rose-500/10', label: 'At Risk' },
  unknown: { icon: Shield, color: 'text-white/50', bg: 'bg-adm-surface/5', label: 'Unknown' },
};

function InsightCard({ insight }) {
  const severityConfig = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.medium;
  const categoryConfig = CATEGORY_CONFIG[insight.category] || CATEGORY_CONFIG.general;
  const CategoryIcon = categoryConfig.icon;
  
  return (
    <Card className={`${severityConfig.bg} ${severityConfig.border} border`}>
      <CardContent className="py-4 px-5">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${severityConfig.bg} border ${severityConfig.border} flex-shrink-0`}>
            <CategoryIcon className={`w-5 h-5 ${severityConfig.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className={`text-sm font-medium ${severityConfig.color}`}>
                {insight.title}
              </h3>
              <Badge className={`${severityConfig.bg} ${severityConfig.color} border-0 text-[9px] px-1.5`}>
                {severityConfig.label}
              </Badge>
              <Badge className="bg-adm-surface/5 text-white/50 border-0 text-[9px] px-1.5">
                {categoryConfig.label}
              </Badge>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              {insight.explanation}
            </p>
            {insight.suggested_action && (
              <div className="mt-3 p-2 rounded bg-adm-surface/5 flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-[#C89B7B] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-white/60">{insight.suggested_action}</p>
              </div>
            )}
            <div className="mt-3 flex items-center gap-4 text-xs text-white/40">
              {insight.confidence && (
                <div className="flex items-center gap-2">
                  <span>Confidence:</span>
                  <div className="h-1.5 w-16 bg-adm-surface/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${severityConfig.bg.replace('/10', '/40')}`}
                      style={{ width: `${insight.confidence * 100}%` }}
                    />
                  </div>
                  <span>{Math.round(insight.confidence * 100)}%</span>
                </div>
              )}
              {insight.time_window_days && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {insight.time_window_days}-day analysis
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InterventionCard({ intervention }) {
  const priorityColors = {
    high: 'border-rose-500/30 bg-rose-500/10',
    medium: 'border-amber-500/30 bg-amber-500/10',
    low: 'border-emerald-500/30 bg-emerald-500/10',
  };
  
  return (
    <Card className={`${priorityColors[intervention.priority] || priorityColors.medium} border`}>
      <CardContent className="py-3 px-4">
        <div className="flex items-start gap-3">
          <Target className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
            intervention.priority === 'high' ? 'text-rose-400' :
            intervention.priority === 'medium' ? 'text-amber-400' : 'text-emerald-400'
          }`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white/80">{intervention.action}</p>
            <p className="text-xs text-white/40 mt-1">
              {intervention.category} • {intervention.rationale}
            </p>
          </div>
          <Badge className={`text-[9px] ${
            intervention.priority === 'high' ? 'bg-rose-500/20 text-rose-400' :
            intervention.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'
          } border-0`}>
            {intervention.priority}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CareTeamInsights() {
  const { subscriberId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriber, setSubscriber] = useState(null);
  const [data, setData] = useState(null);
  const [timeWindow, setTimeWindow] = useState(14);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!subscriberId) return;
      
      setLoading(true);
      try {
        const [userRes, insightsRes] = await Promise.all([
          axios.get(`${API}/users/${subscriberId}`),
          axios.get(`${API}/insights/v2/${subscriberId}/care-team?days=${timeWindow}`)
        ]);
        
        setSubscriber(userRes.data);
        setData(insightsRes.data);
      } catch (error) {
        console.error('Failed to fetch care team insights:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [subscriberId, timeWindow]);
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#C89B7B] animate-spin" />
        </div>
      </AdminLayout>
    );
  }
  
  const riskAlerts = data?.risk_alerts || [];
  const patternInsights = data?.pattern_insights || [];
  const aiSummary = data?.ai_clinical_summary;
  const trendSummary = data?.trend_summary || {};
  const profileContext = data?.profile_context || {};
  const interventions = data?.suggested_interventions || [];
  
  const dominantState = trendSummary.dominant_state || 'unknown';
  const stateConfig = STATE_CONFIG[dominantState] || STATE_CONFIG.unknown;
  const StateIcon = stateConfig.icon;
  
  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="care-team-insights">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            className="text-white/50 hover:text-white mb-4 -ml-2"
            onClick={() => navigate(`/admin/subscribers/${subscriberId}`)}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Subscriber
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#C89B7B]/20 flex items-center justify-center">
                <User className="w-6 h-6 text-[#C89B7B]" />
              </div>
              <div>
                <h1 className="text-2xl font-['Outfit'] font-bold text-white">
                  {subscriber?.name || 'Subscriber'}
                </h1>
                <p className="text-white/50 text-sm">{subscriber?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex gap-2">
                {[7, 14, 30].map(days => (
                  <Button
                    key={days}
                    variant={timeWindow === days ? 'default' : 'outline'}
                    size="sm"
                    className={timeWindow === days 
                      ? 'bg-[#C89B7B] text-[#0B0F19]'
                      : 'border-white/10 text-white/70'
                    }
                    onClick={() => setTimeWindow(days)}
                  >
                    {days}d
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Top Row: State + Trend + Profile Context */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Dominant State */}
          <Card className={`${stateConfig.bg} border-white/10`}>
            <CardContent className="py-5 px-6">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full ${stateConfig.bg} flex items-center justify-center`}>
                  <StateIcon className={`w-6 h-6 ${stateConfig.color}`} />
                </div>
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-wider">Dominant State</p>
                  <p className={`text-xl font-bold ${stateConfig.color}`}>{stateConfig.label}</p>
                </div>
              </div>
              {trendSummary.state_distribution && (
                <div className="mt-4 flex gap-2">
                  {Object.entries(trendSummary.state_distribution).map(([state, pct]) => (
                    <Badge key={state} className={`${STATE_CONFIG[state]?.bg || 'bg-adm-surface/5'} ${STATE_CONFIG[state]?.color || 'text-white/50'} border-0 text-[10px]`}>
                      {state}: {pct}%
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* State Changes */}
          <Card className="bg-[#131B2B] border-white/10">
            <CardContent className="py-5 px-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-wider">State Transitions</p>
                  <p className="text-xl font-bold text-white">{trendSummary.state_changes || 0}</p>
                  <p className="text-xs text-white/40">changes in {timeWindow} days</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Profile Context */}
          <Card className="bg-[#131B2B] border-white/10">
            <CardContent className="py-5 px-6">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Profile Context</p>
              <div className="space-y-2">
                {profileContext.worst_period && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span className="text-white/60">Worst period:</span>
                    <span className="text-white">{profileContext.worst_period}</span>
                  </div>
                )}
                {profileContext.medications?.length > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Pill className="w-4 h-4 text-purple-400" />
                    <span className="text-white/60">Medications:</span>
                    <span className="text-white truncate">{profileContext.medications.join(', ')}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* AI Clinical Summary */}
        {aiSummary && (
          <Card className="bg-gradient-to-br from-[#131B2B] to-[#1A2235] border-indigo-500/20 mb-6">
            <CardContent className="py-5 px-6">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="w-5 h-5 text-indigo-400" />
                <h3 className="text-sm font-medium text-indigo-400 uppercase tracking-wider">AI Clinical Summary</h3>
              </div>
              <div className="prose prose-invert prose-sm max-w-none">
                <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">
                  {aiSummary}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Two Column Layout: Alerts/Patterns + Interventions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Alerts & Patterns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Risk Alerts */}
            {riskAlerts.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-rose-400" />
                  <h2 className="text-lg font-medium text-white">Risk Alerts</h2>
                  <Badge className="bg-rose-500/20 text-rose-400 border-0 ml-2">
                    {riskAlerts.length}
                  </Badge>
                </div>
                <div className="space-y-3">
                  {riskAlerts.map((alert, i) => (
                    <InsightCard key={alert.id || i} insight={alert} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Pattern Insights */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-indigo-400" />
                <h2 className="text-lg font-medium text-white">Pattern Insights</h2>
                <Badge className="bg-indigo-500/20 text-indigo-400 border-0 ml-2">
                  {patternInsights.length}
                </Badge>
              </div>
              {patternInsights.length > 0 ? (
                <div className="space-y-3">
                  {patternInsights.map((pattern, i) => (
                    <InsightCard key={pattern.id || i} insight={pattern} />
                  ))}
                </div>
              ) : (
                <Card className="bg-[#131B2B] border-white/10">
                  <CardContent className="py-8 text-center">
                    <Brain className="w-10 h-10 text-white/10 mx-auto mb-3" />
                    <p className="text-white/50">No patterns detected</p>
                    <p className="text-xs text-white/30 mt-1">Need more tracking data</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
          
          {/* Right Column: Interventions */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-[#C89B7B]" />
              <h2 className="text-lg font-medium text-white">Suggested Interventions</h2>
            </div>
            {interventions.length > 0 ? (
              <div className="space-y-3">
                {interventions.map((intervention, i) => (
                  <InterventionCard key={i} intervention={intervention} />
                ))}
              </div>
            ) : (
              <Card className="bg-[#131B2B] border-white/10">
                <CardContent className="py-8 text-center">
                  <Target className="w-10 h-10 text-white/10 mx-auto mb-3" />
                  <p className="text-white/50">No interventions suggested</p>
                  <p className="text-xs text-white/30 mt-1">Subscriber appears stable</p>
                </CardContent>
              </Card>
            )}
            
            {/* Quick Actions */}
            <div className="mt-6 space-y-2">
              <Button
                variant="outline"
                className="w-full border-white/10 text-white/70 justify-start"
                onClick={() => navigate(`/admin/subscribers/${subscriberId}`)}
              >
                <FileText className="w-4 h-4 mr-2" />
                View Full Profile
              </Button>
              <Button
                variant="outline"
                className="w-full border-white/10 text-white/70 justify-start"
                onClick={() => navigate(`/admin/wearables/${subscriberId}`)}
              >
                <Activity className="w-4 h-4 mr-2" />
                View Wearable Data
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
